import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";

import { AnalyticsDebugPanel } from "@/components/analytics/analytics-debug-panel";
import { AttributionTracker } from "@/components/analytics/attribution-tracker";
import { AnalyticsClickTracker } from "@/components/analytics/click-tracker";
import { CookieConsent } from "@/components/analytics/cookie-consent";
import { GoogleAnalytics } from "@/components/analytics/google-analytics";
import { MetaPixel } from "@/components/analytics/meta-pixel";
import { Footer } from "@/components/footer";
import TanStackQueryLayout from "@/components/integrations/tanstack-query/layout.tsx";
import { Provider as QueryProvider } from "@/components/integrations/tanstack-query/root-provider.tsx";
import { AppTourProvider } from "@/components/tour/AppTourProvider";
import { UploadProgressToast } from "@/components/upload/UploadProgressToast";
import { useUploadStore } from "@/lib/upload/upload-store";

import appCss from "../styles.css?url";

import { Toaster } from "@/components/ui/sonner";
import { registerServiceWorker } from "@/lib/pwa/register-sw";
import { seo } from "@/utils/seo";
import type { QueryClient } from "@tanstack/react-query";
import { ScriptOnce } from "@tanstack/react-router";
import { type ReactNode, useEffect, useRef } from "react";

interface MyRouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        name: "theme-color",
        content: "#09090b",
        media: "(prefers-color-scheme: dark)",
      },
      {
        name: "theme-color",
        content: "#ffffff",
        media: "(prefers-color-scheme: light)",
      },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-title", content: "TheCoach" },
      {
        name: "apple-mobile-web-app-status-bar-style",
        content: "black-translucent",
      },
      ...seo({
        title:
          "Alican Atalay | AtalayTheCoach - Powerlifting Koçluğu ve Spor Forumu | Alican Atalay ile Spor",
        description:
          "Alican Atalay ile powerlifting ve güç sporları üzerine uzman koçluk hizmetleri, Alican Atalay'dan forum tartışmaları ve eğitici blog yazıları. Alican Atalay ile sporcu performansınızı geliştirin. Alican Atalay ile birebir koçluk ve danışmanlık.",
        keywords:
          "Alican Atalay, powerlifting, güç sporları, koçluk, spor forumu, deadlift, squat, bench press, sporcu gelişimi, antrenman programı, beslenme, Alican Atalay koç, Alican Atalay forum, Alican Atalay blog, Alican Atalay danışmanlık",
        image: "/favicon/favicon.svg",
        canonical: "https://atalaythecoach.com",
      }),
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/favicon/apple-touch-icon.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "192x192",
        href: "/favicon/web-app-manifest-192x192.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "512x512",
        href: "/favicon/web-app-manifest-512x512.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "96x96",
        href: "/favicon/favicon-96x96.png",
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/favicon/favicon.svg",
      },
      {
        rel: "manifest",
        href: "/favicon/site.webmanifest",
      },
      {
        rel: "icon",
        href: "/favicon/favicon.ico",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "AtalayTheCoach",
          url: "https://atalaythecoach.com",
          logo: "https://atalaythecoach.com/favicon/favicon.svg",
          sameAs: [
            "https://www.instagram.com/atalaythecoach/",
            "https://www.youtube.com/@atalaythecoach",
          ],
        }),
      },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  const hasActiveUploads = useUploadStore((s) => s.hasActiveUploads);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Ekranın kararmasını engelle (upload sırasında)
  useEffect(() => {
    const active = hasActiveUploads();
    if (active && "wakeLock" in navigator) {
      navigator.wakeLock
        .request("screen")
        .then((lock) => {
          wakeLockRef.current = lock;
        })
        .catch(() => {
          // wakeLock desteklenmiyor veya reddedildi — sessizce geç
        });
    } else if (!active && wakeLockRef.current) {
      wakeLockRef.current.release().catch((_e) => {
        // ignore release errors
      });
      wakeLockRef.current = null;
    }
  }, [hasActiveUploads]);

  // Sekme kapatılmak istenince uyar
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasActiveUploads]);

  return (
    <RootDocument>
      <QueryProvider>
        <AppTourProvider>
          <div className="flex min-h-screen flex-col">
            <AttributionTracker />
            <AnalyticsClickTracker />
            <GoogleAnalytics />
            <MetaPixel />
            <main className="flex-1">
              <Outlet />
            </main>
            <Toaster />
            <UploadProgressToast />
            <CookieConsent />
            <AnalyticsDebugPanel />
            <Footer />
          </div>
        </AppTourProvider>
      </QueryProvider>
      <TanStackRouterDevtools />
      <TanStackQueryLayout />
    </RootDocument>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html suppressHydrationWarning>
      <head suppressHydrationWarning>
        <HeadContent />
      </head>
      <body
        className="bg-background font-regular tracking-wide antialiased"
        suppressHydrationWarning
      >
        <ScriptOnce>
          {`document.documentElement.classList.toggle(
            'dark',
            localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)
            )`}
        </ScriptOnce>
        {children}
        <Scripts />
      </body>
    </html>
  );
}
